import { inject, Injectable, Signal } from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import { from } from "rxjs";

import { FormIOCategoryService } from "@main/formio/services/formioCategory.service";
import { FormIOModuleService } from "@main/formio/services/formio-module.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { FormModuleMinDTO } from "@main/formio/dto/formModuleDTO";
import { Permissions } from "@main/permissions/permissions";

import { FormIODefinition } from "@features/bb-formio";


@Injectable()
export class MultiUserFormAccessService {

    private categoryService: FormIOCategoryService = inject(FormIOCategoryService);
    private userHelperService: UserHelperService = inject(UserHelperService);
    private moduleService: FormIOModuleService = inject(FormIOModuleService);

    private formModules: Signal<FormModuleMinDTO[]> = toSignal(from(this.getModules()));


    public hasBasicAccess(): boolean {
        // Top admins always have access to MUFs.
        if (this.userHelperService.isTopAdmin) return true;

        // Negate access for this user if it has no write access to forms.
        return this.userHelperService.hasPermission(Permissions.WriteFormsInstances);
    }

    public hasAccess(definition: FormIODefinition): boolean {
        if (!definition) return false;

        // Negate access to archived forms.
        if (definition.archived) return false;

        // Negate access if there is no publishing data.
        if (!definition.formPublishingData) return false;

        // Negate access to form if it is not a MUF.
        if (!definition.activeRevision?.mufCapable) return false;

        // Forbid access if the user has no basic access rights.
        if (!this.hasBasicAccess()) return true;

        const moduleName: string = definition.formCategory.name;
        const permissions: Permissions[] = this.categoryService.getWritePermissionsForCategory(moduleName);
        const moduleExists: boolean = this.formModules()?.some(mod => mod.name === moduleName);
        const moduleAccess: boolean = this.userHelperService.hasPermission(`${moduleName} - Write`);

        // If no permissions are specified in the module, but it exists and the user
        // has no explicit "module name - write" special permission, return false.
        if (!permissions && !!moduleExists && !moduleAccess) return false;

        // If module has specific permissions and this user is missing any, return false.
        return !permissions?.length || this.userHelperService.hasPermission(...permissions);
    }

    private async getModules(): Promise<FormModuleMinDTO[]> {
        return await this.moduleService.getAllMin();
    }
}